import React, { useEffect, useMemo, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@material-ui/core'
import {
  AssignmentIndOutlined,
  AssignmentOutlined,
  AssignmentReturnOutlined,
  Close
} from '@material-ui/icons'
import redux from '../redux/index.js'
import { createPeopleActionRunner, handlePeopleCcClick, handlePeopleValueClick } from './people-actions.js'
import {
  FOOTER_BACKGROUND,
  FOOTER_HEIGHT,
  HEADER_BACKGROUND,
  HEADER_HEIGHT
} from '../styles.js'
import { formatResourceString, httpify } from '../util.js'
import {
  isWord,
  fillInContentControls,
  fillInContactContentControls,
  pastePersonData,
  pasteFieldValue,
  setEmailAddressComposeMode,
  isOutlookComposeMode,
  pasteContactData
} from '../lib/office.js'

const onPhoneClick = phone => window.open(`tel:${phone}`)
const PeopleDialog = ({
  locale,
  contactId,
  people,
  title,
  onHidePeopleDialog
}) => {
  const contactDetails = useSelector((state) => state.contact.items.find(({ id }) => id === contactId))
  const composingOutlook = isOutlookComposeMode()
  const canInsert = isWord() || composingOutlook
  const dispatch = useDispatch()
  const [pendingActions, setPendingActions] = useState(new Set())
  const runAction = useMemo(() => createPeopleActionRunner(
    setPendingActions,
    error => dispatch(redux.actions.error.showFeedback(
      error?.errorType || (composingOutlook ? 'ERROR_TYPE_OUTLOOK_OPERATION_FAILED' : 'ERROR_TYPE_WORD_OPERATION_FAILED'),
      error?.message || '',
      'error'
    ))
  ), [dispatch, composingOutlook])
  useEffect(() => {
    if (contactId) {
      dispatch(redux.actions.contact.getContact(contactId))
    }
  }, [dispatch, contactId])

  const renderActions = (key, fill, paste, email, name) => {
    if (!canInsert) return null

    const fillLabel = composingOutlook ? locale.add_email_recipient : locale.fill_content_control

    return (
      <div>
        <Tooltip title={fillLabel}>
          <span>
            <IconButton
              aria-label={fillLabel}
              disabled={pendingActions.has(`${key}-fill`) || (composingOutlook && !email)}
              onClick={() => runAction(`${key}-fill`, fill)}
              style={{ marginRight: 5, padding: 0 }}
            >
              <AssignmentReturnOutlined />
            </IconButton>
          </span>
        </Tooltip>
        {composingOutlook && (
          <Tooltip title={locale.add_email_cc_recipient}>
            <span>
              <IconButton
                aria-label={locale.add_email_cc_recipient}
                disabled={pendingActions.has(`${key}-cc`) || !email}
                onClick={event => handlePeopleCcClick(event, field => runAction(`${key}-cc`, () => setEmailAddressComposeMode([{
                  displayName: name || '',
                  emailAddress: email
                }], field)))}
                style={{ marginRight: 5, padding: 0 }}
              >
                <AssignmentIndOutlined />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={locale.paste_person_data}>
          <span>
            <IconButton
              aria-label={locale.paste_person_data}
              disabled={pendingActions.has(`${key}-paste`)}
              onClick={() => runAction(`${key}-paste`, paste)}
              style={{ padding: 0 }}
            >
              <AssignmentOutlined />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    )
  }

  const renderValue = (key, value, href) => {
    if (!canInsert || value == null || value === '') return value

    const actionKey = `value-${key}`
    const label = formatResourceString(href ? locale.paste_field_link : locale.paste_field_value, value)
    const paste = () => runAction(actionKey, () => pasteFieldValue(value))
    return (
      <Tooltip title={label}>
        <span>
          {href
            ? (
              <a
                href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel='noopener noreferrer'
                aria-label={label}
                aria-disabled={pendingActions.has(actionKey)}
                onClick={event => handlePeopleValueClick(event, paste)}
                style={valueButtonStyle}
              >
                {value}
              </a>
              )
            : (
              <button
                type='button'
                aria-label={label}
                disabled={pendingActions.has(actionKey)}
                onClick={paste}
                style={valueButtonStyle}
              >
                {value}
              </button>
              )}
        </span>
      </Tooltip>
    )
  }

  const renderPersons = () => {
    return people.map((p, i) => {
      // TODO remove person?.collection_name after views have been updated
      const company = p.person_collection_name || p.collection_name

      return (
        <div key={i} style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div><b>{renderValue(`person-${i}-name`, p.person_name)}</b></div>
            {renderActions(`person-${i}`, () => fillInContentControls(p), () => pastePersonData(p), p.person_email, p.person_name)}
          </div>
          <div><b>{renderValue(`person-${i}-company`, company)}</b></div>
          <div><b><i>{p.role_name}</i></b></div>
          <div>{renderValue(`person-${i}-address`, p.person_address)}</div>
          <div>{renderValue(`person-${i}-postal-city`, [p.person_postal_code, p.person_city].filter(Boolean).join(' '))}</div>
          {p.person_email && (
            <div>
              {canInsert
                ? renderValue(`person-${i}-email`, p.person_email, `mailto:${p.person_email}`)
                : (
                  <Tooltip title={locale.open}>
                    <a
                      href={`mailto:${p.person_email}`}
                    >
                      {p.person_email}
                    </a>
                  </Tooltip>
                  )}
            </div>
          )}
          {p.person_phone && (
            <div>
              {canInsert
                ? renderValue(`person-${i}-phone`, p.person_phone, `tel:${p.person_phone}`)
                : (
                  <a href='#' onClick={() => onPhoneClick(p.person_phone)}>{p.person_phone}</a>
                  )}
            </div>
          )}
          {p.person_homepage && (
            <div>
              {canInsert
                ? renderValue(`person-${i}-homepage`, p.person_homepage, httpify(p.person_homepage))
                : <a href={httpify(p.person_homepage)} target='_new'>{p.person_homepage}</a>}
            </div>
          )}
        </div>
      )
    })
  }

  return (
    <Dialog fullScreen open>
      <DialogTitle style={titleStyle}>
        <div style={titleContentStyle}>
          {title || locale.people_dialog_title}
        </div>
      </DialogTitle>
      <DialogContent style={contentStyle}>
        {contactDetails && (
          <div style={addressCard.div}>
            <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 20, marginRight: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div><b><h4 style={addressCard.text}>{renderValue('contact-name', contactDetails.name)}</h4></b></div>
                {renderActions('contact', () => fillInContactContentControls(contactDetails), () => pasteContactData(contactDetails), contactDetails?.address?.email, contactDetails.name)}

              </div>
              <div>
                <p style={addressCard.text}>{renderValue('contact-address', contactDetails?.address?.address)}</p>
                <p style={addressCard.text}>{renderValue('contact-postal-city', [contactDetails?.address?.postal_code, contactDetails?.address?.city].filter(Boolean).join(' '))}</p>
                {contactDetails?.address?.email && (
                  <div>
                    {canInsert
                      ? renderValue('contact-email', contactDetails.address.email, `mailto:${contactDetails.address.email}`)
                      : (
                        <Tooltip title={locale.open}>
                          <a
                            href={`mailto:${contactDetails.address.email}`}
                          >
                            {contactDetails.address.email}
                          </a>
                        </Tooltip>
                        )}
                  </div>
                )}
                {contactDetails?.address?.phone && (
                  <div>
                    {canInsert
                      ? renderValue('contact-phone', contactDetails.address.phone, `tel:${contactDetails.address.phone}`)
                      : (
                        <a href='#' onClick={() => onPhoneClick(contactDetails.address.phone)}>{contactDetails.address.phone}</a>
                        )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div style={{ margin: 20, display: 'flex', flexDirection: 'column' }}>
          {renderPersons()}
        </div>
      </DialogContent>
      <DialogActions style={actionsStyle}>
        <Button
          onClick={onHidePeopleDialog}
          style={closeButtonStyle}
        >
          <Close style={closeIconStyle} />{locale.close}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const valueButtonStyle = {
  background: 'none',
  border: 0,
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer'
}

const titleStyle = {
  background: HEADER_BACKGROUND,
  color: 'white',
  padding: '0px 19px',
  fontSize: '20px',
  height: HEADER_HEIGHT
}

const titleContentStyle = {
  marginTop: '8px'
}

const contentStyle = {
  background: 'white',
  padding: '0px'
}

const actionsStyle = {
  background: FOOTER_BACKGROUND,
  height: FOOTER_HEIGHT,
  padding: 0
}

const closeButtonStyle = {
  color: 'white',
  textTransform: 'none',
  marginRight: '10px'
}

const closeIconStyle = {
  marginRight: '5px'
}

const addressCard = {
  div: {
    borderBottom: '1px solid #dae1e5',
    paddingBottom: '8px',
    paddingTop: '8px'
  },
  text: {
    color: '#4f4f4f',
    margin: 0,
    padding: 0
  }
}

const mapStateToProps = (state) => {
  const { meta } = state.dialog
  return {
    locale: state.locale.strings,
    people: meta.people || [],
    title: meta.title || '',
    contactId: meta.contactId

  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    onHidePeopleDialog: () => {
      dispatch(redux.actions.dialog.hideDialog())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PeopleDialog)
